import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Loader2, Eye, EyeOff } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { useAppDispatch } from '@/app/hooks'
import { fetchCurrentUser, logout } from '@/features/auth/authSlice'
import { useUserRole } from '@/hooks/useUserRole'
import { logoutAndRedirect } from '@/lib/api/authService'
import { updateProfile, changePassword } from '@/lib/api/userService'
import { formatPhoneNumber, unformatPhoneNumber } from '@/types/application'
import { showSuccess, showApiError } from '@/lib/utils/notifications'
import { ValidationModal } from '@/components/ui/ValidationModal'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import type {
  UserProfileFormData,
  UserPasswordFormData,
} from '../types/profile'

/**
 * Name validation pattern (matches Angular UpdateProfileComponent).
 */
const namePattern =
  /^(?! )[a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+(?: [a-zA-Z0-9!@#$%^&*()_+=[\]{}|;:'",.<>?/`~\\-]+){0,2}(?<! )$/

const profileSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First Name is required.')
    .regex(namePattern, 'Enter Valid First Name'),
  lastName: z
    .string()
    .min(1, 'Last Name is required.')
    .regex(namePattern, 'Enter Valid Last Name'),
  mobileNo: z
    .string()
    .min(1, 'Contact No is required.')
    .refine((val) => {
      const digits = val.replace(/\D/g, '')
      return digits.length === 10 || digits.length === 12 || digits.length === 13
    }, 'Enter Valid Contact Phone, Should match pattern [(+X)XXX-XXX-XXXX].'),
  email: z
    .string()
    .optional()
    .refine((val) => !val || z.string().email().safeParse(val).success, {
      message: 'Email is not valid.',
    }),
  jobTitle: z.string().optional(),
  receivedNotificationEmail: z.boolean().optional(),
  showTabIndication: z.boolean().optional(),
})

const passwordSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, 'Current password is required')
      .min(6, 'Password must be at least 6 characters'),
    newPassword: z
      .string()
      .min(1, 'New password is required')
      .min(6, 'Password must be at least 6 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })

/**
 * General user profile page — React equivalent of Angular's `user/profile`
 * (ProfileComponent). Driven by the authenticated user store so it works for
 * every role, unlike the applicant-scoped profile at `/applicant/account`.
 */
export function UserProfilePage() {
  const dispatch = useAppDispatch()
  const { user, roleName, isAdmin, isLibertyRole } = useUserRole()

  const [activeTab, setActiveTab] = useState<'account' | 'password'>('account')
  const [avatarBroken, setAvatarBroken] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false)
  const [redirectCountdown, setRedirectCountdown] = useState(5)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const redirectIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Angular parity: identity fields editable only for ROLE_ADMIN; job title also
  // for ROLE_RENTER; notification preferences shown only for Liberty roles.
  const canEditIdentity = isAdmin
  const canEditJobTitle = isAdmin || roleName === 'ROLE_RENTER'
  const showNotificationPrefs = isLibertyRole

  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    reset: resetProfile,
    formState: { errors: profileErrors, isSubmitting: isSubmittingProfile },
  } = useForm<UserProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      mobileNo: '',
      email: '',
      jobTitle: '',
      receivedNotificationEmail: false,
      showTabIndication: false,
    },
  })

  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    reset: resetPassword,
    formState: { errors: passwordErrors, isSubmitting: isSubmittingPassword },
  } = useForm<UserPasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  useEffect(() => {
    document.title = 'Profile'
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  }, [])

  useEffect(() => {
    setAvatarBroken(false)
  }, [user?.profile_pic])

  // Populate the form from the authenticated user (Angular's user store).
  useEffect(() => {
    if (user) {
      resetProfile({
        firstName: user.first_name || '',
        lastName: user.last_name || '',
        mobileNo: formatPhoneNumber(user.mobile_no),
        email: user.email || '',
        jobTitle: user.job_title || '',
        receivedNotificationEmail: user.received_notification_email === '1',
        showTabIndication: user.show_tab_indication === '1',
      })
    }
  }, [user, resetProfile])

  const displayName = user
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : 'User'

  const getUserInitials = () => {
    const first = user?.first_name?.charAt(0) || ''
    const last = user?.last_name?.charAt(0) || ''
    return `${first}${last}`.toUpperCase() || 'U'
  }

  const onSubmitProfile = async (data: UserProfileFormData) => {
    try {
      // Only send non-empty values (Angular getFilterValue parity).
      const payload: Parameters<typeof updateProfile>[0] = {
        first_name: data.firstName.trim(),
        last_name: data.lastName.trim(),
        mobile_no: unformatPhoneNumber(data.mobileNo),
        ...(data.email?.trim() && { email: data.email.trim().toLowerCase() }),
        ...(data.jobTitle?.trim() && { job_title: data.jobTitle.trim() }),
        ...(showNotificationPrefs &&
          data.receivedNotificationEmail && {
            received_notification_email: '1',
          }),
        ...(showNotificationPrefs &&
          data.showTabIndication && { show_tab_indication: '1' }),
      }

      await updateProfile(payload)
      await dispatch(fetchCurrentUser())
      showSuccess('Profile Updated Successfully')
    } catch (err) {
      showApiError(err)
    }
  }

  const onSubmitPassword = async (data: UserPasswordFormData) => {
    try {
      await changePassword({
        old_password: data.currentPassword,
        new_password: data.newPassword,
        confirm_password: data.confirmPassword,
      })
      resetPassword()
      setRedirectCountdown(5)
      setShowPasswordChangeDialog(true)
    } catch (err) {
      showApiError(err)
    }
  }

  // Angular parity: after a password change the session is invalidated and the
  // user is redirected to login after a 5s countdown.
  const handlePasswordChangeLogout = async () => {
    if (isLoggingOut) return
    setIsLoggingOut(true)
    if (redirectIntervalRef.current) {
      clearInterval(redirectIntervalRef.current)
      redirectIntervalRef.current = null
    }
    dispatch(logout())
    await logoutAndRedirect()
  }

  useEffect(() => {
    if (!showPasswordChangeDialog) return
    redirectIntervalRef.current = setInterval(() => {
      setRedirectCountdown((prev) => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
        redirectIntervalRef.current = null
      }
    }
  }, [showPasswordChangeDialog])

  useEffect(() => {
    if (showPasswordChangeDialog && redirectCountdown === 0) {
      if (redirectIntervalRef.current) {
        clearInterval(redirectIntervalRef.current)
        redirectIntervalRef.current = null
      }
      void handlePasswordChangeLogout()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPasswordChangeDialog, redirectCountdown])

  const inputClass = (hasError: boolean, disabled: boolean) =>
    cn(
      'w-full px-4 py-2.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
      hasError ? 'border-red-500' : 'border-gray-300',
      disabled && 'opacity-50 cursor-not-allowed bg-gray-100',
    )

  return (
    <div
      data-testid="user-profile-page"
      className="min-h-screen bg-gray-50 font-sans"
    >
      <PageHeader
        title="PROFILE"
        impersonationStopDataTestId="user-profile-stop-impersonate-button"
        useApplicantImpersonationFallback
      />

      <div className="px-4 sm:px-6 md:px-8 py-6 sm:py-8">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left — profile picture */}
            <div className="lg:col-span-1 flex items-center justify-center">
              <div className="flex flex-col items-center justify-center">
                <div className="w-48 h-48 rounded-full bg-gray-200 flex items-center justify-center mb-6 border-4 border-gray-300 overflow-hidden">
                  {user?.profile_pic && !avatarBroken ? (
                    <img
                      src={user.profile_pic}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={() => setAvatarBroken(true)}
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-teal-600 flex items-center justify-center">
                      <span className="text-white text-6xl font-bold">
                        {getUserInitials()}
                      </span>
                    </div>
                  )}
                </div>

                <h2 className="text-2xl font-bold text-gray-900 text-center">
                  {displayName}
                </h2>
              </div>
            </div>

            {/* Right — account / password tabs */}
            <div className="lg:col-span-2">
              <div className="flex gap-1 border-b border-gray-200 mb-6">
                <button
                  type="button"
                  onClick={() => setActiveTab('account')}
                  className={cn(
                    'px-6 py-3 font-semibold text-sm transition-colors relative',
                    activeTab === 'account'
                      ? 'text-[#1597ff]'
                      : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  Account
                  {activeTab === 'account' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1597ff]" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('password')}
                  className={cn(
                    'px-6 py-3 font-semibold text-sm transition-colors relative',
                    activeTab === 'password'
                      ? 'text-[#1597ff]'
                      : 'text-gray-600 hover:text-gray-900',
                  )}
                >
                  Change Password
                  {activeTab === 'password' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1597ff]" />
                  )}
                </button>
              </div>

              {activeTab === 'account' ? (
                <form onSubmit={handleSubmitProfile(onSubmitProfile)}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Firstname<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...registerProfile('firstName')}
                        disabled={!canEditIdentity}
                        className={inputClass(
                          !!profileErrors.firstName,
                          !canEditIdentity,
                        )}
                      />
                      {profileErrors.firstName && (
                        <p className="mt-1 text-sm text-red-600">
                          {profileErrors.firstName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Lastname<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...registerProfile('lastName')}
                        disabled={!canEditIdentity}
                        className={inputClass(
                          !!profileErrors.lastName,
                          !canEditIdentity,
                        )}
                      />
                      {profileErrors.lastName && (
                        <p className="mt-1 text-sm text-red-600">
                          {profileErrors.lastName.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Contact No<span className="text-red-500">*</span>
                      </label>
                      <input
                        type="tel"
                        placeholder="(123) 456-7890"
                        disabled={!canEditIdentity}
                        {...registerProfile('mobileNo')}
                        className={inputClass(
                          !!profileErrors.mobileNo,
                          !canEditIdentity,
                        )}
                      />
                      {profileErrors.mobileNo && (
                        <p className="mt-1 text-sm text-red-600">
                          {profileErrors.mobileNo.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Email
                      </label>
                      <input
                        type="email"
                        {...registerProfile('email')}
                        disabled={!canEditIdentity}
                        className={inputClass(
                          !!profileErrors.email,
                          !canEditIdentity,
                        )}
                      />
                      {profileErrors.email && (
                        <p className="mt-1 text-sm text-red-600">
                          {profileErrors.email.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Job Title
                      </label>
                      <input
                        type="text"
                        {...registerProfile('jobTitle')}
                        disabled={!canEditJobTitle}
                        className={inputClass(false, !canEditJobTitle)}
                      />
                    </div>
                  </div>

                  {showNotificationPrefs && (
                    <div className="mt-6 space-y-3">
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          {...registerProfile('receivedNotificationEmail')}
                          className="h-4 w-4 rounded border-gray-300 text-[#1597ff] focus:ring-[#1597ff]"
                        />
                        Receive emails about notifications
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700">
                        <input
                          type="checkbox"
                          {...registerProfile('showTabIndication')}
                          className="h-4 w-4 rounded border-gray-300 text-[#1597ff] focus:ring-[#1597ff]"
                        />
                        Show tab indication about notifications
                      </label>
                    </div>
                  )}

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={isSubmittingProfile}
                      className={cn(
                        'px-8 py-3 bg-[#1597ff] text-white font-semibold rounded-md transition-colors shadow-md',
                        isSubmittingProfile
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[#0d7acc] hover:shadow-lg',
                      )}
                    >
                      {isSubmittingProfile ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Saving...
                        </span>
                      ) : (
                        'Submit'
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmitPassword(onSubmitPassword)}>
                  <div className="space-y-6 max-w-2xl">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Current Password<span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? 'text' : 'password'}
                          {...registerPassword('currentPassword')}
                          disabled={isSubmittingPassword}
                          className={cn(
                            inputClass(
                              !!passwordErrors.currentPassword,
                              false,
                            ),
                            'pr-12',
                            isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed',
                          )}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowCurrentPassword(!showCurrentPassword)
                          }
                          disabled={isSubmittingPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          {showCurrentPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.currentPassword && (
                        <p className="mt-1 text-sm text-red-600">
                          {passwordErrors.currentPassword.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        New Password<span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? 'text' : 'password'}
                          {...registerPassword('newPassword')}
                          disabled={isSubmittingPassword}
                          className={cn(
                            inputClass(!!passwordErrors.newPassword, false),
                            'pr-12',
                            isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed',
                          )}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          disabled={isSubmittingPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          {showNewPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.newPassword && (
                        <p className="mt-1 text-sm text-red-600">
                          {passwordErrors.newPassword.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2 uppercase">
                        Confirm Password<span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? 'text' : 'password'}
                          {...registerPassword('confirmPassword')}
                          disabled={isSubmittingPassword}
                          className={cn(
                            inputClass(
                              !!passwordErrors.confirmPassword,
                              false,
                            ),
                            'pr-12',
                            isSubmittingPassword &&
                              'opacity-50 cursor-not-allowed',
                          )}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          disabled={isSubmittingPassword}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-5 w-5" />
                          ) : (
                            <Eye className="h-5 w-5" />
                          )}
                        </button>
                      </div>
                      {passwordErrors.confirmPassword && (
                        <p className="mt-1 text-sm text-red-600">
                          {passwordErrors.confirmPassword.message}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={isSubmittingPassword}
                      className={cn(
                        'px-8 py-3 bg-[#1597ff] text-white font-semibold rounded-md transition-colors shadow-md',
                        isSubmittingPassword
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-[#0d7acc] hover:shadow-lg',
                      )}
                    >
                      {isSubmittingPassword ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Changing...
                        </span>
                      ) : (
                        'Change Password'
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>

      <ValidationModal
        isOpen={showPasswordChangeDialog}
        title="Password Changed"
        message={
          isLoggingOut
            ? 'Logging out... Please wait while we redirect you.'
            : `Current Session Expired. You will be redirected to login in ${redirectCountdown} sec. Please log in again.`
        }
        variant="success"
        confirmText={isLoggingOut ? 'Redirecting...' : 'OK'}
        onClose={() => void handlePasswordChangeLogout()}
      />
    </div>
  )
}
